import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { getProject } from '../api/projects'
import {
  AiMode,
  createAiSession,
  deleteAiSession,
  getAiModels,
  getAiSettings,
  getAiSessionMessages,
  getAiSessions,
  sendAiMessage,
  updateAiSettings,
} from '../api/ai'

const MODE_LABELS: Record<AiMode, string> = {
  question: 'Вопрос',
  prepare: 'Подготовка изменений',
  apply: 'Применить сразу',
}

function sanitizeMessageInput(raw: string): string {
  return raw.replace(/^(?:\s*undefined)+/gi, '').trimStart()
}

export default function AiProjectPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()

  const [searchParams, setSearchParams] = useSearchParams()

  const [activeSessionId, setActiveSessionId] = useState<number | null>(null)
  const [isSessionMenuOpen, setIsSessionMenuOpen] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [selectedMode, setSelectedMode] = useState<AiMode>('question')
  const [selectedModel, setSelectedModel] = useState('auto')
  const [pendingFiles, setPendingFiles] = useState<File[]>([])

  const numericProjectId = Number(projectId)

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(numericProjectId),
    enabled: !!projectId,
  })

  const { data: modelsData, isLoading: modelsLoading } = useQuery({
    queryKey: ['ai-models', projectId],
    queryFn: () => getAiModels(numericProjectId),
    enabled: !!projectId,
  })

  const { data: settingsData } = useQuery({
    queryKey: ['ai-settings', projectId],
    queryFn: () => getAiSettings(numericProjectId),
    enabled: !!projectId,
  })

  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ['ai-sessions', projectId],
    queryFn: () => getAiSessions(numericProjectId),
    enabled: !!projectId,
  })

  const querySessionId = searchParams.get('session_id') ? Number(searchParams.get('session_id')) : null

  // Sync querySessionId to activeSessionId
  useEffect(() => {
    if (querySessionId && querySessionId !== activeSessionId) {
      const exists = sessions.some((s) => s.id === querySessionId)
      if (exists) {
        setActiveSessionId(querySessionId)
      }
    }
  }, [querySessionId, sessions, activeSessionId])

  // Sync querySessionId to activeSessionId on load
  useEffect(() => {
    if (querySessionId && querySessionId !== activeSessionId) {
      const exists = sessions.some((s) => s.id === querySessionId)
      if (exists) {
        setActiveSessionId(querySessionId)
      }
    }
  }, [querySessionId, sessions, activeSessionId])

  useEffect(() => {
    if (!activeSessionId && sessions.length > 0) {
      if (querySessionId) {
        const exists = sessions.some((s) => s.id === querySessionId)
        if (exists) {
          setActiveSessionId(querySessionId)
          return
        }
      }
      setActiveSessionId(sessions[0].id)
      setSearchParams({ session_id: String(sessions[0].id) }, { replace: true })
    }
  }, [sessions, activeSessionId, querySessionId, setSearchParams])

  useEffect(() => {
    if (!modelsData?.popular_models?.length) return
    const hasSelected = modelsData.models.some((m) => m.id === selectedModel)
    if (!hasSelected) {
      setSelectedModel(modelsData.popular_models[0].id)
    }
  }, [modelsData, selectedModel])

  useEffect(() => {
    if (!settingsData) return
    let modeToSet = settingsData.preferred_mode || 'question'
    if (settingsData.allowed_modes && !settingsData.allowed_modes.includes(modeToSet)) {
      modeToSet = settingsData.allowed_modes.length > 0 ? settingsData.allowed_modes[0] : 'question'
    }
    setSelectedMode(modeToSet as AiMode)
    if (settingsData.preferred_model) {
      setSelectedModel(settingsData.preferred_model)
    }
  }, [settingsData])

  const updateSettingsMutation = useMutation({
    mutationFn: (payload: { preferred_mode: AiMode; preferred_model?: string | null }) =>
      updateAiSettings({
        project_id: numericProjectId,
        preferred_mode: payload.preferred_mode,
        preferred_model: payload.preferred_model ?? null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['ai-settings', projectId] })
    },
  })

  const createSessionMutation = useMutation({
    mutationFn: async () => {
      const title = `Сессия ${new Date().toLocaleString('ru-RU')}`
      return createAiSession({
        project_id: numericProjectId,
        title,
        selected_model: selectedModel,
      })
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['ai-sessions', projectId] })
      setActiveSessionId(result.id)
      setSearchParams({ session_id: String(result.id) }, { replace: true })
    },
  })

  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: number) => deleteAiSession(sessionId),
    onSuccess: async (_result, deletedSessionId) => {
      if (activeSessionId === deletedSessionId) {
        const nextSession = sessions.find((session) => session.id !== deletedSessionId)
        const nextId = nextSession ? nextSession.id : null
        setActiveSessionId(nextId)
        if (nextId) {
          setSearchParams({ session_id: String(nextId) }, { replace: true })
        } else {
          const newParams = new URLSearchParams(searchParams)
          newParams.delete('session_id')
          setSearchParams(newParams, { replace: true })
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['ai-sessions', projectId] })
      await queryClient.invalidateQueries({ queryKey: ['ai-session-thread', deletedSessionId] })
    },
  })

  const { data: threadData, isLoading: messagesLoading } = useQuery({
    queryKey: ['ai-session-thread', activeSessionId],
    queryFn: () => getAiSessionMessages(activeSessionId!),
    enabled: !!activeSessionId,
    refetchInterval: 2000,
  })

  const sendMessageMutation = useMutation({
    mutationFn: async () => {
      const normalizedContent = sanitizeMessageInput(messageText).trim()
      if (!normalizedContent) {
        throw new Error('Введите текст запроса')
      }

      let sessionId = activeSessionId
      if (!sessionId) {
        const created = await createAiSession({
          project_id: numericProjectId,
          title: `Сессия ${new Date().toLocaleString('ru-RU')}`,
          selected_model: selectedModel,
        })
        sessionId = created.id
        setActiveSessionId(created.id)
        setSearchParams({ session_id: String(created.id) }, { replace: true })
      }
      const payload = await sendAiMessage(sessionId, {
        content: normalizedContent,
        mode: selectedMode,
        model: selectedModel,
        files: pendingFiles,
      })

      return payload
    },
    onSuccess: async () => {
      setMessageText('')
      setPendingFiles([])
      await queryClient.invalidateQueries({ queryKey: ['ai-session-thread', activeSessionId] })
      await queryClient.invalidateQueries({ queryKey: ['ai-sessions', projectId] })
    },
  })

  const sortedMessages = useMemo(() => threadData?.messages ?? [], [threadData])
  const latestJob = useMemo(() => (threadData?.jobs ?? [])[0] ?? null, [threadData])
  const latestJobDurationSec = useMemo(() => {
    if (!latestJob?.started_at) return null
    const started = new Date(latestJob.started_at).getTime()
    const ended = latestJob.completed_at ? new Date(latestJob.completed_at).getTime() : Date.now()
    if (!Number.isFinite(started) || !Number.isFinite(ended) || ended < started) return null
    return Math.round((ended - started) / 1000)
  }, [latestJob])
  const isJobRunning = latestJob?.status === 'running'
  const modelOptions = useMemo(() => {
    if (!modelsData) return []
    const primary = modelsData.popular_models || []
    if (primary.length > 0) return primary
    return modelsData.models || []
  }, [modelsData])
  const activeSessionTitle = useMemo(() => {
    if (!activeSessionId) return null
    return sessions.find((session) => session.id === activeSessionId)?.title ?? null
  }, [sessions, activeSessionId])

  const canSend = sanitizeMessageInput(messageText).trim().length > 0 && !sendMessageMutation.isPending

  const onModeChange = (mode: AiMode) => {
    setSelectedMode(mode)
    updateSettingsMutation.mutate({ preferred_mode: mode, preferred_model: selectedModel })
  }

  const onModelChange = (model: string) => {
    setSelectedModel(model)
    updateSettingsMutation.mutate({ preferred_mode: selectedMode, preferred_model: model })
  }

  const onMessageChange = (value: string) => {
    setMessageText(sanitizeMessageInput(value))
  }

  const onSubmit = () => {
    if (sendMessageMutation.isPending) {
      return
    }
    sendMessageMutation.mutate()
  }

  const onDeleteSession = (sessionId: number) => {
    if (deleteSessionMutation.isPending) {
      return
    }
    const confirmed = window.confirm('Удалить эту сессию? Это действие нельзя отменить.')
    if (!confirmed) {
      return
    }
    deleteSessionMutation.mutate(sessionId)
  }

  if (settingsData && settingsData.allowed_modes && settingsData.allowed_modes.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
              ← Назад к проекту
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">ИИ-агенты проекта</h1>
            <p className="text-gray-600 mt-1">{project?.name}</p>
          </div>
        </div>
        <div className="bg-red-50 text-red-600 p-4 rounded-lg border border-red-200">
          У вас нет доступа к веб-ИИ-агенту. Обратитесь к администратору для получения соответствующих прав.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
            ← Назад к проекту
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">ИИ-агенты проекта</h1>
          <p className="text-gray-600 mt-1">{project?.name}</p>
        </div>
        <button
          onClick={() => createSessionMutation.mutate()}
          disabled={createSessionMutation.isPending}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {createSessionMutation.isPending ? 'Создание...' : '➕ Новая сессия'}
        </button>
      </div>

      <div className="md:hidden">
        <button
          onClick={() => setIsSessionMenuOpen(true)}
          className="w-full px-4 py-2 bg-white border border-gray-200 rounded-md text-sm text-gray-800 text-left shadow-sm"
        >
          🗂 Сессии{activeSessionTitle ? `: ${activeSessionTitle}` : ''}
        </button>
      </div>

      {isSessionMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            onClick={() => setIsSessionMenuOpen(false)}
            aria-label="Закрыть меню сессий"
          />
          <aside className="absolute left-0 right-0 top-0 max-h-[75vh] bg-white rounded-b-xl shadow-xl p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-800">Сессии</h2>
              <button
                type="button"
                onClick={() => setIsSessionMenuOpen(false)}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Закрыть
              </button>
            </div>
            {sessionsLoading ? (
              <div className="text-sm text-gray-500">Загрузка...</div>
            ) : sessions.length === 0 ? (
              <div className="text-sm text-gray-500">Пока нет сессий</div>
            ) : (
              <div className="space-y-2">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`rounded-md border transition-colors ${
                      activeSessionId === session.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <button
                      onClick={() => {
                        setActiveSessionId(session.id)
                        setSearchParams({ session_id: String(session.id) }, { replace: true })
                        setIsSessionMenuOpen(false)
                      }}
                      className="w-full text-left p-3"
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">{session.title}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {session.last_message || 'Без сообщений'}
                      </div>
                    </button>
                    <div className="px-3 pb-3">
                      <button
                        type="button"
                        onClick={() => onDeleteSession(session.id)}
                        disabled={deleteSessionMutation.isPending}
                        className="text-xs text-red-600 hover:text-red-700 underline disabled:opacity-50"
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4 min-h-[70vh]">
        <aside className="hidden md:block md:col-span-3 bg-white rounded-lg shadow-md p-3 overflow-y-auto">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Сессии</h2>
          {sessionsLoading ? (
            <div className="text-sm text-gray-500">Загрузка...</div>
          ) : sessions.length === 0 ? (
            <div className="text-sm text-gray-500">Пока нет сессий</div>
          ) : (
            <div className="space-y-2">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className={`rounded-md border transition-colors ${
                    activeSessionId === session.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <button
                    onClick={() => {
                      setActiveSessionId(session.id)
                      setSearchParams({ session_id: String(session.id) }, { replace: true })
                    }}
                    className="w-full text-left p-3"
                  >
                    <div className="text-sm font-medium text-gray-900 truncate">{session.title}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {session.last_message || 'Без сообщений'}
                    </div>
                  </button>
                  <div className="px-3 pb-3">
                    <button
                      type="button"
                      onClick={() => onDeleteSession(session.id)}
                      disabled={deleteSessionMutation.isPending}
                      className="text-xs text-red-600 hover:text-red-700 underline disabled:opacity-50"
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>

        <section className="col-span-12 md:col-span-9 bg-white rounded-lg shadow-md flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Модель:</label>
              <select
                value={selectedModel}
                onChange={(e) => onModelChange(e.target.value)}
                disabled={modelsLoading}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
              >
                {modelOptions.length === 0 && (
                  <option value="auto">Auto</option>
                )}
                {modelOptions.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Режим:</span>
              {(['question', 'prepare', 'apply'] as AiMode[]).map((mode) => {
                const isAllowed = settingsData?.allowed_modes?.includes(mode) ?? true
                if (!isAllowed) return null
                return (
                  <label key={mode} className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="ai-mode"
                      value={mode}
                      checked={selectedMode === mode}
                      onChange={() => onModeChange(mode)}
                    />
                    {MODE_LABELS[mode]}
                  </label>
                )
              })}
            </div>
            {latestJob && (
              <>
                <div className="ml-auto text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
                  Статус: {latestJob.status}
                  {latestJobDurationSec !== null ? ` • ${latestJobDurationSec}с` : ''}
                </div>
                {isJobRunning && (
                  <div className="ml-auto flex items-center gap-2 text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 sticky top-2 z-20 shadow-sm">
                    <span className="inline-block h-3 w-3 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
                    <span>Магия в процессе: происходит множество обращений к данным проекта...</span>
                  </div>
                )}
              </>
            )}
          </div>
          {modelOptions.length === 0 && (
            <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-200">
              Список моделей временно недоступен, используем модель по умолчанию `auto`.
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messagesLoading ? (
              <div className="text-sm text-gray-500">Загрузка сообщений...</div>
            ) : sortedMessages.length === 0 ? (
              <div className="text-sm text-gray-500">Сессия пуста. Отправьте первый запрос.</div>
            ) : (
              sortedMessages.map((message) => (
                <div
                  key={message.id}
                  className={`max-w-[90%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'ml-auto bg-blue-600 text-white'
                      : message.status === 'error'
                      ? 'bg-red-100 text-red-900'
                      : 'bg-white border border-gray-200 text-gray-900'
                  }`}
                >
                  <div className="text-xs opacity-80 mb-1">
                    {message.role === 'user' ? 'Вы' : 'Агент'}
                    {message.mode ? ` • ${MODE_LABELS[message.mode]}` : ''}
                    {message.model ? ` • ${message.model}` : ''}
                  </div>
                  {message.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1 prose-ul:my-1 prose-ol:my-1">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm, remarkBreaks]}
                        components={{
                          a: ({node, ...props}) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold underline hover:text-blue-800" />
                          )
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap text-sm">{message.content}</div>
                  )}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="mt-2 text-xs opacity-85">
                      Вложения: {message.attachments.map((a) => a.original_filename).join(', ')}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="border-t border-gray-200 p-4 space-y-3">
            <textarea
              value={messageText}
              onChange={(e) => onMessageChange(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault()
                  onSubmit()
                }
              }}
              rows={4}
              placeholder="Опишите задачу для AI-агента..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-600 cursor-pointer">
                  📎 Файлы
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => setPendingFiles(Array.from(e.target.files || []))}
                  />
                </label>
                {pendingFiles.length > 0 && (
                  <div className="text-xs text-gray-600">
                    {pendingFiles.length} файл(ов): {pendingFiles.map((f) => f.name).join(', ')}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMessageText('')
                    setPendingFiles([])
                  }}
                  className="text-xs text-gray-600 hover:text-gray-800 underline"
                >
                  Очистить ввод
                </button>
              </div>
              <button
                onClick={onSubmit}
                disabled={!canSend}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {sendMessageMutation.isPending ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
            {sendMessageMutation.isError && (
              <div className="text-sm text-red-600">
                Ошибка отправки: {(sendMessageMutation.error as Error)?.message || 'неизвестная ошибка'}
              </div>
            )}
            {latestJob?.status === 'failed' && latestJob.error_text && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                Ошибка выполнения задачи: {latestJob.error_text}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}
